import { Check, Settings, Calendar, RefreshCw, Loader2, Minus } from "lucide-react";
import { useContext, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";
import EmployeeCalComModal from "./EmployeeCalComModal";

type EmployeeCalComCardProps = {
  employee: {
    id: string;
    name: string;
    phone: string;
    email?: string;
    calCom?: {
      apiKey?: string;
      username?: string;
      eventTypeId?: string;
      teamId?: string;
      webhookId?: string;
      webhookTriggers?: string[];
    };
  };
};

// What syncing actually changes in Palomma. BOOKING_CREATED is also subscribed,
// but only so a visit the agent booked can be recognised when Cal.com echoes it
// back — it creates nothing, so listing it here would promise something the
// webhook does not do. Triggers without an entry are not shown for the same reason.
const EVENT_LABELS: Record<string, string> = {
  BOOKING_CANCELLED: "Citas canceladas",
  BOOKING_RESCHEDULED: "Citas reprogramadas",
};

const EmployeeCalComCard = ({ employee }: EmployeeCalComCardProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isConfigured = !!(employee.calCom?.apiKey && employee.calCom?.eventTypeId);
  const isSubscribed = !!employee.calCom?.webhookId;

  const handleSyncSettled = () => utils.employees.list.invalidate({ merchantId });

  const { mutate: subscribe, isLoading: isSubscribing } =
    trpc.employees.subscribeCalWebhook.useMutation({
      onSuccess: () => {
        toast.success("Sincronización activada", {
          description:
            "Las cancelaciones y reprogramaciones hechas en Cal.com se reflejarán en las citas.",
        });
      },
      onError: (error) => {
        toast.error("Error al activar la sincronización", {
          description: error.message || "Ocurrió un error al conectar con Cal.com.",
        });
      },
      onSettled: handleSyncSettled,
    });

  const { mutate: unsubscribe, isLoading: isUnsubscribing } =
    trpc.employees.unsubscribeCalWebhook.useMutation({
      onSuccess: () => {
        toast.success("Sincronización desactivada", {
          description: "Los cambios hechos en Cal.com ya no actualizarán las citas.",
        });
      },
      onError: (error) => {
        toast.error("Error al desactivar la sincronización", {
          description: error.message || "Ocurrió un error al conectar con Cal.com.",
        });
      },
      onSettled: handleSyncSettled,
    });

  const isSyncing = isSubscribing || isUnsubscribing;

  // Subscribed: what Cal.com actually registered. Not subscribed: what activating
  // would cover — so the card explains the consequence either way.
  const syncedEvents = (
    employee.calCom?.webhookTriggers?.length
      ? employee.calCom.webhookTriggers
      : Object.keys(EVENT_LABELS)
  ).filter((event) => EVENT_LABELS[event]);

  const handleToggleSync = () => {
    const input = { merchantId, employeeId: employee.id };
    if (isSubscribed) {
      unsubscribe(input);
      return;
    }
    subscribe(input);
  };

  return (
    <>
      <div
        className={`relative rounded-xl p-6 transition-all hover:shadow-md ${
          isConfigured
            ? "border-2 border-green-400 bg-green-50"
            : "border border-gray-200 bg-white"
        }`}
      >
        {isConfigured && (
          <div className="absolute right-4 top-4">
            <Check size={24} color="#48BB78" />
          </div>
        )}

        <div className="mb-4 flex flex-row items-start gap-4">
          <div className="flex min-h-14 min-w-14 items-center justify-center rounded-xl bg-gray-50 p-3">
            <Calendar size={28} color="#4A5568" />
          </div>

          <div className="flex flex-1 flex-col gap-1">
            <p className="text-lg font-bold">
              {employee.name}
            </p>
            <p className="text-sm text-gray-600">
              {employee.email || employee.phone}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            className="w-full"
          >
            {isConfigured && <Settings className="size-4" />}
            {isConfigured ? "Configurar" : "Configurar Cal.com"}
          </Button>

          {isConfigured && (
            <Button
              variant={isSubscribed ? "ghost" : "main"}
              size="sm"
              onClick={handleToggleSync}
              disabled={isSyncing}
              className="w-full"
            >
              {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              {isSubscribed ? "Desactivar sincronización" : "Activar sincronización"}
            </Button>
          )}
        </div>

        {isConfigured && (
          <div
            className={`mt-3 rounded-lg border p-3 ${
              isSubscribed ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <p
              className={`text-sm font-medium ${
                isSubscribed ? "text-green-700" : "text-gray-700"
              }`}
            >
              {isSubscribed ? "Sincronización activa" : "Sincronización inactiva"}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {isSubscribed
                ? "Estos cambios hechos en Cal.com se reflejan en Palomma:"
                : "Sin activar, estos cambios hechos en Cal.com no se reflejan en Palomma:"}
            </p>

            <ul className="mt-2 flex flex-col gap-1">
              {syncedEvents.map((event) => (
                <li key={event} className="flex flex-row items-center gap-2 text-sm">
                  {isSubscribed ? (
                    <Check className="size-4 shrink-0 text-green-600" />
                  ) : (
                    <Minus className="size-4 shrink-0 text-gray-400" />
                  )}
                  <span className={isSubscribed ? "text-gray-700" : "text-gray-500"}>
                    {EVENT_LABELS[event]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <EmployeeCalComModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        employee={employee}
      />
    </>
  );
};

export default EmployeeCalComCard;
